import { type ProductizedService_productizedService$key } from '#app/__generated__/relay/ProductizedService_productizedService.graphql.js';
import { animated } from '#app/components/Primitives/Animated.js';
import { ServiceBanner } from '#app/components/ServiceLaunchBanners/ServiceBanner.js';
import { ServicePageHeader } from '#app/components/ServicePageHeader/ServicePageHeader.js';
import { ServicePageReviews } from '#app/components/ServicePageReviews/ServicePageReviews.js';
import { useIsElementVisible } from '#app/hooks/useIsElementVisible.js';
import { useIsMediumScreen } from '#app/hooks/useIsMediumScreen.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { springAnimation } from '#app/utilities/animation.js';
import { ProductizedServiceCTAs } from './components/ProductizedServiceCTAs/ProductizedServiceCTAs.js';
import { ProductizedServiceDetails } from './components/ProductizedServiceDetails/ProductizedServiceDetails.js';
import { ProductizedServiceIndustries } from './components/ProductizedServiceIndustries/ProductizedServiceIndustries.js';
import { ProductizedServiceMoreServices } from './components/ProductizedServiceMoreServices/ProductizedServiceMoreServices.js';
import { ProductizedServicePrice } from './components/ProductizedServicePrice/ProductizedServicePrice.js';
import { ProductizedServiceSkillsTools } from './components/ProductizedServiceSkillsTools/ProductizedServiceSkillsTools.js';
import { ProductizedServiceTabs } from './components/ProductizedServiceTabs/ProductizedServiceTabs.js';
import { ProductizedServiceWorkWithMe } from './components/ProductizedServiceWorkWithMe/ProductizedServiceWorkWithMe.js';
import { graphql, useFragment } from '@contra/relay';
import { AnimatePresence } from 'framer-motion';
import { type ReactNode } from 'react';
import { css, styled } from 'styled-components';

const Container = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 32px;

    ${theme.mediaQueries.lg} {
      gap: 48px;
    }

    /* If a section is empty, we could end up with multiple breaks */
    > hr + hr {
      display: none;
    }
  `}
`;

const MobileOnly = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 32px;

    ${theme.mediaQueries.lg} {
      display: none;
    }
  `}
`;

// Note: We're intentionally using the `useIsMobile` here instead of
// a media query so that we can correctly render the thematic breaks
const MobileOnlyContainer = ({
  children,
}: {
  readonly children: ReactNode;
}) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <MobileOnly>{children}</MobileOnly>;
  }

  return null;
};

const ThematicBreak = styled.hr`
  ${({ theme }) => css`
    border: none;
    border-top: 1px solid ${theme.colorsV2.gray30};
  `}
`;

const MobileOnlyThematicBreak = () => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <ThematicBreak />;
  }

  return null;
};

const MobileStickyFooter = styled(animated.div)`
  ${({ theme }) => css`
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: ${theme.zIndices.menu};
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    background-color: ${theme.colorsV2.white};
    border-top: 1px solid ${theme.colorsV2.gray30};
    padding: 8px 16px;

    ${theme.mediaQueries.lg} {
      display: none;
    }
  `}
`;

const animation = {
  animate: {
    transform: 'translateY(0)',
  },
  exit: {
    transform: 'translateY(100%)',
  },
  initial: {
    transform: 'translateY(100%)',
  },
  transition: springAnimation.fast,
};

type ProductizedServiceProps = {
  readonly productizedServiceRef: ProductizedService_productizedService$key;
};

export const ProductizedService = ({
  productizedServiceRef,
}: ProductizedServiceProps) => {
  const service = useFragment<ProductizedService_productizedService$key>(
    graphql`
      fragment ProductizedService_productizedService on ProductizedService {
        slug
        updatedAt
        visitorCanEdit
        userProfile {
          id
          ...ProductizedServiceWorkWithMe_userProfile
          visitorCanEdit
          avatarImage {
            id
            url
            uid
          }
        }
        ...ProductizedServicePrice_productizedService
        ...ProductizedServiceDeliverables_productizedService
        ...ProductizedServiceExampleProjects_productizedService
        ...ProductizedServiceMoreServices_productizedService
        ...ProductizedServiceDetails_productizedService
        ...ProductizedServiceIndustries_productizedService
        ...ProductizedServiceSkillsTools_productizedService
        ...ProductizedServiceCTAs_productizedService
        ...ServiceBanner_productizedService
        ...ServicePageReviews_productizedService
        ...ServicePageHeader_productizedService
        ...ProductizedServiceTabs_productizedService
        ...ProductizedServiceWorkWithMe_productizedService

        introductionVideo {
          isEnabled
          video {
            uid
          }
        }
      }
    `,
    productizedServiceRef,
  );
  // This hook is only used on mobile.
  const [inlinePriceRef, inlinePriceIsVisible] =
    useIsElementVisible<HTMLDivElement>({ threshold: 0.2 }, true);

  const isMobile = useIsMobile();
  const isMediumScreen = useIsMediumScreen();

  return (
    <Container>
      {isMediumScreen && <ServiceBanner productizedServiceRef={service} />}

      <ServicePageHeader productizedServiceRef={service} />

      {!isMediumScreen && <ServiceBanner productizedServiceRef={service} />}

      <MobileOnlyContainer>
        <ProductizedServicePrice
          productizedServiceRef={service}
          ref={inlinePriceRef}
        />
        <ProductizedServiceCTAs productizedServiceRef={service} />
      </MobileOnlyContainer>

      <ProductizedServiceTabs productizedServiceRef={service} />
      <ThematicBreak />

      <MobileOnlyContainer>
        <ProductizedServiceDetails productizedServiceRef={service} />
        <ProductizedServiceSkillsTools productizedServiceRef={service} />
        <ProductizedServiceIndustries productizedServiceRef={service} />
      </MobileOnlyContainer>
      <MobileOnlyThematicBreak />
      {!isMobile && <ServicePageReviews productizedServiceRef={service} />}
      <ProductizedServiceWorkWithMe
        canBuyNow={false}
        productizedServiceRef={service}
        slug={service.slug}
        userProfileRef={service.userProfile}
      />
      <ThematicBreak />
      <ProductizedServiceMoreServices productizedServiceRef={service} />
      <AnimatePresence>
        {inlinePriceIsVisible ? null : (
          <MobileStickyFooter {...animation}>
            <ProductizedServicePrice
              productizedServiceRef={service}
              size="sm"
            />
            <ProductizedServiceCTAs
              productizedServiceRef={service}
              variant="INLINE"
            />
          </MobileStickyFooter>
        )}
      </AnimatePresence>
    </Container>
  );
};
